/**
 * AgentBroadcastPreviewModal.tsx
 *
 * Operator-side preview for the AI agent broadcast canvas (slice 4c).
 *
 * Lets the operator:
 *   - Watch the synthesised Ken-Burns avatar + caption animation in
 *     real time.
 *   - Push test captions through the engine without burning Azure
 *     TTS budget (text-only — no real audio synthesis).
 *   - See the live caption queue with timestamps.
 *
 * This intentionally does NOT initiate a real WebRTC broadcast —
 * the integration with `webrtcBroadcastService.startBroadcast()`
 * lands in slice 4e once the paid-session payment flow has been
 * adapted to the agent identity. The point of this modal is to
 * validate that the visual / audio pipeline works end-to-end so
 * the MCP server (slice 4d) has a clean target to drive.
 */

import React, { useEffect, useMemo, useState } from 'react';
import AgentBroadcastCanvas, {
  useAgentBroadcastEngine,
  useAgentBroadcastStats,
  type CaptionEntry,
  type AgentBroadcastEngineStats,
} from './AgentBroadcastCanvas';
import {
  pushTestCaption,
  getSpeechStatus,
  type SpeechStatus,
} from '../services/agentBroadcastService';

interface AgentBroadcastPreviewModalProps {
  agentPublicKey: string;
  displayName: string | null;
  avatarUrl: string | null;
  open: boolean;
  onClose: () => void;
}

const AgentBroadcastPreviewModal: React.FC<AgentBroadcastPreviewModalProps> = ({
  agentPublicKey,
  displayName,
  avatarUrl,
  open,
  onClose,
}) => {
  const { engine, captions } = useAgentBroadcastEngine({
    agentPublicKey,
    displayName,
    avatarUrl,
  });
  const engineStats = useAgentBroadcastStats(engine, 1000);

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const [speechStatus, setSpeechStatus] = useState<SpeechStatus | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getSpeechStatus()
      .then((s) => { if (!cancelled) setSpeechStatus(s); })
      .catch(() => { if (!cancelled) setSpeechStatus(null); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const handlePush = async () => {
    setErr(null);
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await pushTestCaption(engine, t);
      setText('');
    } catch (e) {
      const msg = (e as Error)?.message || 'Failed to push caption';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Agent broadcast preview">
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🤖</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                Broadcast preview
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#f9fafb' }}>
                {displayName || agentPublicKey.slice(0, 8) + '…'}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
        </div>

        <div style={canvasWrapStyle}>
          <AgentBroadcastCanvas engine={engine} />
        </div>

        <div style={controlsStyle}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 500))}
            placeholder="Type a test caption — visuals only (no TTS, no Azure cost)"
            rows={2}
            style={inputStyle}
            disabled={busy}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={charCountStyle}>{text.length}/500</span>
            <button
              type="button"
              onClick={() => void handlePush()}
              disabled={busy || !text.trim()}
              style={primaryBtnStyle}
            >
              {busy ? 'Pushing…' : 'Push test caption'}
            </button>
          </div>
          {err && <div style={errorStyle}>{err}</div>}
        </div>

        <div style={statusBarStyle}>
          <SpeechStatusBadge status={speechStatus} />
          <CanvasAliveBadge stats={engineStats} />
          <CaptionQueueSummary captions={captions} />
        </div>
      </div>
    </div>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

const SpeechStatusBadge: React.FC<{ status: SpeechStatus | null }> = ({ status }) => {
  if (!status) {
    return <span style={badgeMutedStyle}>Speech: checking…</span>;
  }
  if (!status.configured) {
    return (
      <span style={badgeWarnStyle} title="Set AGENT_SPEECH_AZURE_KEY + AGENT_SPEECH_AZURE_REGION on the server">
        Speech: not configured
      </span>
    );
  }
  return (
    <span style={badgeOkStyle} title={`Region ${status.region} · default ${status.defaultVoice}`}>
      Speech: ready ({status.region})
    </span>
  );
};

/**
 * Render-pipeline health badge. Mirrors the live-broadcast modal so
 * the operator can verify the synthetic canvas is actually drawing
 * frames before opening a paid live session.
 */
const CanvasAliveBadge: React.FC<{ stats: AgentBroadcastEngineStats | null }> = ({ stats }) => {
  if (!stats) {
    return <span style={badgeMutedStyle}>Canvas: starting…</span>;
  }
  if (stats.disposed) {
    return (
      <span style={badgeWarnStyle} title="Render engine disposed; re-open the modal to restart it.">
        Canvas: stopped
      </span>
    );
  }
  const ageMs = stats.lastTickAt
    ? Math.max(0, performance.now() - stats.lastTickAt)
    : Number.POSITIVE_INFINITY;
  const stalled = ageMs > 1500;
  if (stalled || stats.tickCount === 0) {
    return (
      <span
        style={badgeWarnStyle}
        title={`Last frame ${Number.isFinite(ageMs) ? Math.round(ageMs) + 'ms' : 'never'} ago.`}
      >
        Canvas: stalled
      </span>
    );
  }
  return (
    <span
      style={badgeOkStyle}
      title={`${stats.tickCount.toLocaleString()} frames rendered, last ${Math.round(ageMs)}ms ago.`}
    >
      Canvas: live ({stats.tickCount.toLocaleString()}f)
    </span>
  );
};

const CaptionQueueSummary: React.FC<{ captions: CaptionEntry[] }> = ({ captions }) => {
  const latest = useMemo(() => captions[captions.length - 1] ?? null, [captions]);
  if (!latest) return <span style={badgeMutedStyle}>0 captions</span>;
  return (
    <span style={badgeMutedStyle} title={latest.text}>
      {captions.length} caption{captions.length === 1 ? '' : 's'} • latest: "
      {latest.text.length > 28 ? `${latest.text.slice(0, 28)}…` : latest.text}"
    </span>
  );
};

// ── Inline styles ──────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.78)',
  zIndex: 10_000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  backdropFilter: 'blur(4px)',
};

const panelStyle: React.CSSProperties = {
  width: 'min(900px, 100%)',
  maxHeight: '92vh',
  background: '#0f172a',
  color: '#e5e7eb',
  borderRadius: 16,
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 18px',
  borderBottom: '1px solid #1e293b',
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#9ca3af',
  fontSize: 28,
  lineHeight: 1,
  cursor: 'pointer',
  padding: 4,
};

const canvasWrapStyle: React.CSSProperties = {
  padding: 16,
  background: '#020617',
  display: 'flex',
  justifyContent: 'center',
};

const controlsStyle: React.CSSProperties = {
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  borderTop: '1px solid #1e293b',
};

const inputStyle: React.CSSProperties = {
  background: '#020617',
  border: '1px solid #334155',
  borderRadius: 8,
  color: '#f9fafb',
  padding: '10px 12px',
  resize: 'vertical',
  fontSize: 14,
  fontFamily: 'inherit',
};

const primaryBtnStyle: React.CSSProperties = {
  background: '#6366f1',
  border: '1px solid #4f46e5',
  color: '#fff',
  fontWeight: 600,
  padding: '8px 16px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
};

const charCountStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#94a3b8',
};

const errorStyle: React.CSSProperties = {
  background: '#7f1d1d',
  border: '1px solid #b91c1c',
  color: '#fee2e2',
  padding: '8px 12px',
  borderRadius: 8,
  fontSize: 13,
};

const statusBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '10px 18px',
  background: '#020617',
  borderTop: '1px solid #1e293b',
  fontSize: 12,
};

const badgeBase: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 999,
  fontWeight: 500,
};

const badgeMutedStyle: React.CSSProperties = {
  ...badgeBase,
  background: '#1e293b',
  color: '#94a3b8',
};

const badgeOkStyle: React.CSSProperties = {
  ...badgeBase,
  background: '#064e3b',
  color: '#bbf7d0',
};

const badgeWarnStyle: React.CSSProperties = {
  ...badgeBase,
  background: '#78350f',
  color: '#fed7aa',
};

export default AgentBroadcastPreviewModal;
